import { useState } from "react";
import { Phone, Mail, MapPin, Clock, ArrowUpRight, CheckCircle2, MessageCircle, Linkedin, Facebook, Instagram, Globe } from "lucide-react";
import { contact, brand, principals, social } from "../data/mindscope";
import { Reveal } from "../components/mindscope/Reveal";
import BrandLogo from "../components/mindscope/BrandLogo";

const focusOptions = [
  "IPO / Public Markets",
  "Rights / Preferential / QIP",
  "Buy Back / Delisting / OFS",
  "NCD / Debt Instruments",
  "AIF Registration",
  "M&A · Share Swap · Restructuring",
  "Listing Regulation Advisory",
  "Something else — let's talk",
];

const socialLinks = [
  { Icon: Linkedin, label: "LinkedIn", href: social.linkedin, testid: "social-linkedin" },
  { Icon: Facebook, label: "Facebook", href: social.facebook, testid: "social-facebook" },
  { Icon: Instagram, label: "Instagram", href: social.instagram, testid: "social-instagram" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", enterprise: "", email: "", phone: "", focus: focusOptions[0], message: "" });
  const [submitted, setSubmitted] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setForm({ name: "", enterprise: "", email: "", phone: "", focus: focusOptions[0], message: "" });
  };

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <main data-testid="contact-page" className="bg-ms-navy text-white overflow-hidden">
      {/* HERO */}
      <section className="pt-40 pb-24 bg-ms-navy grain relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-ms-gold/5 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-ms-navy-mid/40 blur-3xl" />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <Reveal className="lg:col-span-8">
              <div className="eyebrow mb-6">Chapter Seven — Begin Dialogue</div>
              <h1 className="font-display text-6xl md:text-8xl lg:text-[10rem] leading-[0.9] tracking-[-0.03em]">
                A single <span className="italic text-ms-gold">conversation</span>.
              </h1>
              <p className="mt-8 max-w-2xl font-body text-white/70 text-lg leading-relaxed">
                Often the first instrument in a durable capital relationship. Reach a principal in
                the way that suits your discretion — a message, a call, or a note by post.
              </p>
            </Reveal>
            <Reveal className="lg:col-span-4 hidden lg:flex justify-end" delay={0.2}>
              <BrandLogo variant="transparent" className="h-40 w-auto opacity-70" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* PRINCIPALS */}
      <section className="section-y bg-[#050914]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="eyebrow mb-6">Speak to a Principal</div>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[0.95] mb-14 max-w-3xl">
              Two people, <span className="italic text-ms-gold">on the other end of the line</span>.
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6">
            {principals.map((p, i) => (
              <Reveal key={p.email} delay={i * 0.08}>
                <div
                  data-testid={`principal-${p.name.split(' ')[0].toLowerCase()}`}
                  className="p-8 md:p-10 bg-[#0A1128] border border-white/5 hover:border-ms-gold/50 transition-colors duration-500 flex flex-col h-full"
                >
                  <div className="text-[0.65rem] tracking-[0.32em] uppercase text-ms-gold mb-3">
                    {p.role}
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl text-white leading-tight mb-6">
                    {p.name}
                  </h3>

                  <dl className="space-y-3 font-body text-sm text-white/80 flex-1">
                    <div className="flex items-center gap-3">
                      <Phone size={14} className="text-ms-gold shrink-0" strokeWidth={1.6} />
                      <a href={`tel:${p.tel}`} className="hover:text-ms-gold transition-colors">
                        {p.phone}
                      </a>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail size={14} className="text-ms-gold shrink-0" strokeWidth={1.6} />
                      <a href={`mailto:${p.email}`} className="hover:text-ms-gold transition-colors">
                        {p.email}
                      </a>
                    </div>
                  </dl>

                  <div className="mt-8 flex flex-wrap gap-3">
                    <a
                      href={p.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`principal-whatsapp-${p.name.split(' ')[0].toLowerCase()}`}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] text-ms-navy text-[0.7rem] tracking-[0.24em] uppercase font-medium hover:brightness-110 transition-[filter,transform] duration-300 hover:-translate-y-0.5"
                    >
                      <MessageCircle size={14} strokeWidth={2} />
                      WhatsApp
                    </a>
                    <a
                      href={`mailto:${p.email}`}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-ms-gold text-ms-gold text-[0.7rem] tracking-[0.24em] uppercase hover:bg-ms-gold hover:text-ms-navy transition-colors duration-300"
                    >
                      <Mail size={14} strokeWidth={1.8} />
                      Write directly
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FORM + OFFICE */}
      <section className="section-y bg-ms-navy">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-7">
            <div className="bg-[#14264A] border border-white/5 p-8 md:p-12">
              {submitted ? (
                <div className="min-h-[420px] flex flex-col items-start justify-center" data-testid="contact-success">
                  <CheckCircle2 size={48} className="text-ms-gold mb-6" strokeWidth={1.2} />
                  <h3 className="font-display text-4xl md:text-5xl text-white leading-tight mb-4">
                    A note has been <span className="italic text-ms-gold">received</span>.
                  </h3>
                  <p className="font-body text-white/70 max-w-md leading-relaxed">
                    A principal will review your brief and respond within one working day. All
                    correspondence remains under our standard practice discretion.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    data-testid="contact-send-another"
                    className="mt-8 btn-ghost"
                  >
                    Send Another
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                  <div className="eyebrow mb-2">The Brief</div>
                  <h3 className="font-display text-3xl md:text-4xl text-white leading-tight">
                    Tell us where you are.
                  </h3>
                  <p className="font-body text-white/60 pb-4 border-b border-white/10">
                    A few sentences are enough. We&apos;ll take the first call from there.
                  </p>

                  <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Your Name" value={form.name} onChange={setField("name")} required testid="field-name" />
                    <Field label="Enterprise" value={form.enterprise} onChange={setField("enterprise")} testid="field-enterprise" />
                    <Field label="Email" type="email" value={form.email} onChange={setField("email")} required testid="field-email" />
                    <Field label="Phone" type="tel" value={form.phone} onChange={setField("phone")} testid="field-phone" />
                  </div>
                  <div>
                    <label className="font-body text-[0.7rem] tracking-[0.28em] uppercase text-white/60 block mb-2">
                      Area of Focus
                    </label>
                    <select
                      value={form.focus}
                      onChange={setField("focus")}
                      data-testid="field-focus"
                      className="w-full bg-transparent border-b border-white/20 focus:border-ms-gold outline-none py-3 font-body text-white transition-colors duration-300"
                    >
                      {focusOptions.map((o) => (
                        <option key={o} value={o} className="bg-ms-navy text-white">{o}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="font-body text-[0.7rem] tracking-[0.28em] uppercase text-white/60 block mb-2">
                      Message
                    </label>
                    <textarea
                      value={form.message}
                      onChange={setField("message")}
                      rows={5}
                      data-testid="field-message"
                      className="w-full bg-transparent border border-white/15 focus:border-ms-gold outline-none p-4 font-body text-white resize-none transition-colors duration-300 rounded-sm"
                      placeholder="A few sentences on where you are in your capital journey…"
                    />
                  </div>
                  <button type="submit" className="btn-gold" data-testid="contact-submit">
                    Send Brief <ArrowUpRight size={16} />
                  </button>
                  <p className="font-body text-xs text-white/40 pt-2">
                    Sent in confidence. Reviewed by a principal, never a bot.
                  </p>
                </form>
              )}
            </div>
          </Reveal>

          <Reveal className="lg:col-span-5 lg:col-start-8" delay={0.15}>
            <div className="space-y-1" id="office">
              <div className="p-8 md:p-10 bg-[#050914] border border-white/5 flex flex-col items-start gap-6">
                <BrandLogo variant="transparent" className="h-14 w-auto" />
                <div>
                  <p className="font-devanagari text-xl text-ms-gold mb-2">{brand.taglineSanskrit}</p>
                  <p className="font-display italic text-white/60">{brand.taglineEnglish}</p>
                </div>
              </div>

              <InfoBlock
                icon={<MapPin size={18} />}
                label="Office"
                testid="info-address"
                lines={[contact.address.line1, contact.address.line2, contact.address.line3]}
              />

              <InfoLine icon={<Clock size={18} />} label="Hours" value={contact.hours} testid="info-hours" />

              <InfoLine
                icon={<Globe size={18} />}
                label="Website"
                value={contact.website}
                href={`https://${contact.website.replace(/^www\./, "")}`}
                testid="info-website"
              />

              {/* Social */}
              <div className="p-6 md:p-7 bg-[#14264A] border border-white/5">
                <div className="font-body text-[0.65rem] tracking-[0.28em] uppercase text-white/50 mb-4">Follow</div>
                <div className="flex items-center gap-3">
                  {socialLinks.map(({ Icon, label, href, testid }) => (
                    <a
                      key={testid}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      data-testid={testid}
                      className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-white/75 hover:text-ms-navy hover:bg-ms-gold hover:border-ms-gold transition-colors duration-500"
                    >
                      <Icon size={16} strokeWidth={1.6} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MAP-esque strip */}
      <section className="relative bg-[#050914] border-t border-white/10 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(201,169,97,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(201,169,97,0.08) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="eyebrow mb-3">Find Us</div>
            <h3 className="font-display text-3xl md:text-4xl text-white leading-tight">
              Nagpur · <span className="italic text-ms-gold">Central India&apos;s</span> capital corridor
            </h3>
          </div>
          <a
            href={`https://maps.google.com/?q=${encodeURIComponent(contact.address.line1 + ", " + contact.address.line2)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-map-link"
            className="btn-gold"
          >
            Open in Maps <ArrowUpRight size={16} />
          </a>
        </div>
      </section>
    </main>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="font-body text-[0.7rem] tracking-[0.28em] uppercase text-white/60 block mb-2">
        {label}{required ? " *" : ""}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border-b border-white/20 focus:border-ms-gold outline-none py-3 font-body text-white transition-colors duration-300"
      />
    </div>
  );
}

function InfoLine({ icon, label, value, href, testid }) {
  const Body = (
    <div className="p-6 md:p-7 bg-[#14264A] border border-white/5 flex items-start gap-4 gold-border-hover">
      <div className="text-ms-gold mt-1">{icon}</div>
      <div className="flex-1">
        <div className="font-body text-[0.65rem] tracking-[0.28em] uppercase text-white/50 mb-1">{label}</div>
        <div className="font-display text-lg text-white leading-snug">{value}</div>
      </div>
      {href ? <ArrowUpRight size={16} className="text-white/40 mt-1" /> : null}
    </div>
  );
  return href ? (
    <a href={href} data-testid={testid} className="block">{Body}</a>
  ) : (
    <div data-testid={testid}>{Body}</div>
  );
}

function InfoBlock({ icon, label, lines, testid }) {
  return (
    <div data-testid={testid} className="p-6 md:p-7 bg-[#14264A] border border-white/5 flex items-start gap-4 gold-border-hover">
      <div className="text-ms-gold mt-1">{icon}</div>
      <div className="flex-1">
        <div className="font-body text-[0.65rem] tracking-[0.28em] uppercase text-white/50 mb-2">{label}</div>
        {lines.map((l, i) => (
          <div key={i} className={`font-display text-lg leading-snug ${i === lines.length - 1 ? "text-white/50 text-base" : "text-white"}`}>
            {l}
          </div>
        ))}
      </div>
    </div>
  );
}
